import { useEffect, useRef, useCallback } from 'react';
import { useGrid } from '../../context/GridContext';
import { useCommand } from '../../context/CommandContext';
import { PlantCircle } from './objects/PlantCircle';
import { CreateObjectCommand } from '../../commands/CreateObjectCommand';
import { snapValue } from './tools/snapUtils';

/**
 * PlantDragHandler - Manages drag-and-drop plant placement from Favorites Panel
 *
 * Responsibilities:
 * - Handle dragover events on canvas container (enable dropping)
 * - Show visual feedback during drag (handled by CompactPlantCard)
 * - Handle drop events on canvas container
 * - Create PlantCircle on drop with CreateObjectCommand (undo/redo)
 * - Snap to grid during drop (if enabled, Ctrl overrides)
 * - Update overlap detection after drop
 *
 * Technical Details:
 * - Uses HTML5 Drag-and-Drop API (no external library)
 * - Drag source: CompactPlantCard in Favorites Panel
 * - Drop target: Canvas container div (NOT canvas element - Fabric.js has upper-canvas layer)
 * - Plant data transferred via dataTransfer as JSON
 *
 * Integration:
 * - Used by GardenCanvas component
 * - Works with CompactPlantCard as drag source
 * - Only active on "Pflanzen" tab
 */
const PlantDragHandler = ({ canvas, containerRef, isActive }) => {
  const { snapEnabled, gridSize } = useGrid();
  const { commandStack } = useCommand();

  const dragOverCountRef = useRef(0); // Track dragenter/dragleave for proper hover state

  /**
   * Snap position to grid if snap is enabled and Ctrl is not pressed
   */
  const getSnappedPosition = useCallback((x, y, ctrlPressed) => {
    if (!snapEnabled || ctrlPressed) {
      return { x, y };
    }

    return {
      x: snapValue(x, gridSize, true, false),
      y: snapValue(y, gridSize, true, false),
    };
  }, [snapEnabled, gridSize]);

  /**
   * Handle dragover event - required to enable dropping
   * Must call preventDefault() to allow drop
   */
  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();

    // Set dropEffect to indicate copy operation
    e.dataTransfer.dropEffect = 'copy';
  }, []);

  /**
   * Handle dragenter event - visual feedback when entering canvas
   */
  const handleDragEnter = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();

    dragOverCountRef.current++;

    // Add visual feedback - add border to container
    if (dragOverCountRef.current === 1 && containerRef.current) {
      // Find the actual canvas element inside container
      const canvasElement = containerRef.current.querySelector('canvas');
      if (canvasElement) {
        canvasElement.style.boxShadow = '0 0 0 2px #10B981'; // Emerald-500
      }
    }
  }, [containerRef]);

  /**
   * Handle dragleave event - remove visual feedback when leaving canvas
   */
  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();

    dragOverCountRef.current--;

    // Remove visual feedback when fully leaving canvas
    if (dragOverCountRef.current === 0 && containerRef.current) {
      const canvasElement = containerRef.current.querySelector('canvas');
      if (canvasElement) {
        canvasElement.style.boxShadow = '';
      }
    }
  }, [containerRef]);

  /**
   * Handle drop event - place plant at drop position
   */
  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();

    // Reset drag state
    dragOverCountRef.current = 0;
    if (containerRef.current) {
      const canvasElement = containerRef.current.querySelector('canvas');
      if (canvasElement) {
        canvasElement.style.boxShadow = '';
      }
    }

    if (!canvas) return;

    try {
      // Get plant data from dataTransfer
      const plantData = e.dataTransfer.getData('application/json');
      if (!plantData) {
        console.warn('No plant data in drop event');
        return;
      }

      const plant = JSON.parse(plantData);

      // Lawn plants (growth_type === 'lawn') are area objects drawn as polygons,
      // not placeable as individual plant circles.
      if (plant.properties?.growth_type === 'lawn') {
        return;
      }

      // Convert drop position to canvas coordinates (account for zoom/pan)
      const pointer = canvas.getScenePoint(e);

      // Snap to grid if enabled (Ctrl overrides)
      const ctrlPressed = e.ctrlKey;
      const snappedPos = getSnappedPosition(pointer.x, pointer.y, ctrlPressed);

      // Default color = first flower_color from DB (parity with PlantPlacementManager click path)
      const flowerColors = plant.properties?.flower_color;
      const defaultColor = Array.isArray(flowerColors) && flowerColors.length > 0 ? flowerColors[0] : null;

      const plantCircle = new PlantCircle(plant, {
        left: snappedPos.x,
        top: snappedPos.y,
        selectedColor: defaultColor,
      });

      // Use CreateObjectCommand for undo/redo support
      // Note: CreateObjectCommand.execute() will automatically call updateOverlapStates()
      const command = new CreateObjectCommand(canvas, plantCircle);
      commandStack.execute(command);

      // Select the newly placed plant
      canvas.setActiveObject(plantCircle);
      canvas.requestRenderAll();
    } catch (error) {
      console.error('Failed to handle plant drop:', error);
    }
  }, [canvas, containerRef, commandStack, getSnappedPosition]);

  /**
   * Setup drag-and-drop event listeners on canvas container
   */
  useEffect(() => {
    if (!canvas || !containerRef.current || !isActive) {
      return;
    }

    const containerElement = containerRef.current;

    // Add event listeners to container (not canvas - Fabric.js has upper-canvas layer)
    containerElement.addEventListener('dragover', handleDragOver);
    containerElement.addEventListener('dragenter', handleDragEnter);
    containerElement.addEventListener('dragleave', handleDragLeave);
    containerElement.addEventListener('drop', handleDrop);

    // Cleanup
    return () => {
      containerElement.removeEventListener('dragover', handleDragOver);
      containerElement.removeEventListener('dragenter', handleDragEnter);
      containerElement.removeEventListener('dragleave', handleDragLeave);
      containerElement.removeEventListener('drop', handleDrop);

      // Reset visual state
      dragOverCountRef.current = 0;
      // Use the captured containerElement ref to avoid stale closure warning
      const canvasElement = containerElement.querySelector('canvas');
      if (canvasElement) {
        canvasElement.style.boxShadow = '';
      }
    };
  }, [canvas, containerRef, isActive, handleDragOver, handleDragEnter, handleDragLeave, handleDrop]);

  // This component renders nothing - it only manages drag-and-drop events
  return null;
};

export default PlantDragHandler;
